// views/Geradoras/Analise/RankingUsinas.tsx
//
// A resposta para "a usina está gerando o esperado?". Compara todas as usinas
// no MESMO período em kWh/kWp — a única normalização que deixa uma usina de
// 1,1 MWp e uma de 77 kWp na mesma régua — e marca o desvio contra a referência
// escolhida.
import React from 'react';
import { ArrowDown, ArrowUp, Minus, CircleHelp, TriangleAlert } from 'lucide-react';
import {
    classificarDesvio, desvioPercentual, referenciaEsperada,
    type ContextoReferencia, type MetaGeradora, type ModoReferencia, type StatusDesvio,
} from '../geradorasSeries';

export interface LinhaRanking {
    site_id_api: string;
    nome: string;
    marca: string;
    kwh: number | null;
    kwhKwp: number | null;
    capacidadeMwp: number | null;
    diasComDado: number;
}

const ESTILO: Record<StatusDesvio, { chip: string; barra: string; icone: React.ReactNode; label: string }> = {
    acima: {
        chip: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        barra: 'bg-emerald-500', icone: <ArrowUp size={11} className="stroke-[3]" />, label: 'Acima',
    },
    esperado: {
        chip: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
        barra: 'bg-primary', icone: <Minus size={11} className="stroke-[3]" />, label: 'No esperado',
    },
    abaixo: {
        chip: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
        barra: 'bg-amber-500', icone: <ArrowDown size={11} className="stroke-[3]" />, label: 'Abaixo',
    },
    critico: {
        chip: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800',
        barra: 'bg-rose-500', icone: <TriangleAlert size={11} className="stroke-[3]" />, label: 'Crítico',
    },
    sem_referencia: {
        chip: 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700',
        barra: 'bg-gray-300 dark:bg-gray-600', icone: <CircleHelp size={11} className="stroke-[3]" />, label: 'Sem régua',
    },
};

const ROTULO_MODO: Record<ModoReferencia, string> = {
    irmas: 'mediana das outras usinas',
    propria: 'a própria usina no período anterior',
    meta: 'meta cadastrada do mês',
    ano_anterior: 'mesmo período do ano passado',
};

interface Props {
    linhas: LinhaRanking[];
    modo: ModoReferencia;
    /** kWh/kWp de cada usina no período de contraste (réguas 'propria'/'ano_anterior'). */
    contrastePorSite?: Record<string, number | null>;
    metas?: MetaGeradora[];
    mesDeReferencia: number;
    /**
     * kWp de cada usina no CADASTRO e tamanho do período. A régua 'meta' precisa
     * dos dois para trazer o laudo (normalizado por outra potência, e mensal) à
     * mesma base do realizado — ver metaNoPeriodo.
     */
    capacidadeKwpPorSite?: Record<string, number | null>;
    diasDoPeriodo?: number;
    diasDoMesDeReferencia?: number;
    siteSelecionado?: string | null;
    onSelecionar?: (site: string) => void;
}

const RankingUsinas: React.FC<Props> = ({
    linhas, modo, contrastePorSite, metas, mesDeReferencia, siteSelecionado, onSelecionar,
    capacidadeKwpPorSite, diasDoPeriodo, diasDoMesDeReferencia,
}) => {
    const kwhKwpPorSite: Record<string, number | null> = {};
    linhas.forEach(l => { kwhKwpPorSite[l.site_id_api] = l.kwhKwp; });

    const avaliadas = linhas.map(l => {
        const ctx: ContextoReferencia = {
            kwhKwpPorSite,
            kwhKwpContraste: contrastePorSite?.[l.site_id_api] ?? null,
            metas,
            mesDeReferencia,
            capacidadeKwpPorSite,
            diasDoPeriodo,
            diasDoMesDeReferencia,
        };
        const esperado = referenciaEsperada(l.site_id_api, modo, ctx);
        return {
            ...l,
            esperado,
            desvio: desvioPercentual(l.kwhKwp, esperado),
            status: classificarDesvio(l.kwhKwp, esperado),
        };
    }).sort((a, b) => (b.kwhKwp ?? -1) - (a.kwhKwp ?? -1));

    const maxKwhKwp = Math.max(...avaliadas.map(a => a.kwhKwp ?? 0), 0.0001);
    const problemas = avaliadas.filter(a => a.status === 'abaixo' || a.status === 'critico').length;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-poppins font-bold text-gray-900 dark:text-white">Desempenho por usina</h3>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            kWh por kWp instalado · régua: {ROTULO_MODO[modo]}
                        </p>
                    </div>
                    {problemas > 0 && (
                        <span className="px-2.5 py-1 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 text-[11px] font-bold border border-rose-200 dark:border-rose-800">
                            {problemas} abaixo da régua
                        </span>
                    )}
                </div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700/50 max-h-[26rem] overflow-y-auto">
                {avaliadas.length === 0 && (
                    <div className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                        Sem dados de geração no período.
                    </div>
                )}
                {avaliadas.map((a, i) => {
                    const est = ESTILO[a.status];
                    const selecionada = a.site_id_api === siteSelecionado;
                    return (
                        <button
                            key={a.site_id_api}
                            onClick={() => onSelecionar?.(a.site_id_api)}
                            className={`w-full text-left px-5 py-3 transition-colors border-l-4 ${selecionada
                                ? 'bg-primary/5 border-primary'
                                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-[10px] font-bold text-gray-300 dark:text-gray-600 tabular-nums w-5">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{a.marca}</span>
                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border uppercase tracking-wide ${est.chip}`}>
                                            {est.icone}
                                            {a.desvio != null ? `${a.desvio > 0 ? '+' : ''}${a.desvio.toFixed(0)}%` : est.label}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${est.barra} transition-all duration-500`}
                                            style={{ width: `${Math.max(((a.kwhKwp ?? 0) / maxKwhKwp) * 100, 1.5)}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                        {a.kwhKwp != null ? a.kwhKwp.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}
                                        <span className="text-[10px] text-gray-400 font-semibold ml-1">kWh/kWp</span>
                                    </div>
                                    <div className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                                        {a.kwh != null ? `${a.kwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh` : 'sem dado'}
                                        {a.esperado != null && ` · esperado ${a.esperado.toFixed(2)}`}
                                    </div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default RankingUsinas;
